/**
 * The equipment a movement needs, as photo tiles four across.
 *
 * The backend ships a picture for every piece the catalogue names, out of `SeedAssets/equipment`
 * beside the exercise covers, so the tile draws the THING rather than the first letter of its word.
 * The letter is the fallback for a piece with no picture and for one whose picture 404s — a failed
 * `<img>` with `alt=""` is not nothing on screen, it is Chrome's broken-file mark.
 *
 * The pieces' names are backend DATA, already localised. Never i18n.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import photoBox from '~/shared/ui/photoBox.module.css';

import type { EquipmentThumb } from '../model/exerciseRow';

import styles from './EquipmentTiles.module.css';

function EquipmentTile({ piece }: { readonly piece: EquipmentThumb }) {
  const [failed, setFailed] = useState(false);

  return (
    <span className={styles.tile}>
      <span className={styles.plate}>
        {piece.imageUrl === null || failed ? (
          <i className={styles.letter} aria-hidden>
            {piece.name.slice(0, 1).toUpperCase()}
          </i>
        ) : (
          <img
            className={`${String(photoBox.photo)} ${String(styles.photo)}`}
            src={piece.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => {
              setFailed(true);
            }}
          />
        )}
      </span>
      <span className={styles.label}>{piece.name}</span>
    </span>
  );
}

export function EquipmentTiles({ pieces }: { readonly pieces: readonly EquipmentThumb[] }) {
  const { t } = useTranslation();

  // A bodyweight movement needs nothing, and saying so is a different statement from an empty card.
  if (pieces.length === 0) {
    return <p className={styles.empty}>{t('web:exerciseLibrary.row.noEquipment')}</p>;
  }

  return (
    <div className={styles.grid}>
      {pieces.map((piece) => (
        <EquipmentTile key={piece.id} piece={piece} />
      ))}
    </div>
  );
}
